/**
 * Auth state + route-access predicate (T026, W8).
 *
 * `resolveRouteAccess` is a PURE predicate (table-tested in Vitest without a
 * router); `beforeLoad` in the router is a thin adapter over it (T035). The
 * W8 distinction is load-bearing:
 *   - fetch throw / status ≥500 / timeout  → ApiUnavailable (show reload screen,
 *     do NOT bounce to /login)
 *   - 401 after a failed refresh           → Unauthenticated (redirect to /login)
 *   - 403                                  → authenticated, per-code error (not here)
 */
import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { devAutoLogin } from './devAutoLogin'
import { getMe, refresh } from './endpoints/auth'
import { ApiError } from './errors'
import { qk } from './queryKeys'
import type { UserProfileResponse } from './schemas'

export type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: UserProfileResponse }
  | { status: 'unauthenticated' }
  | { status: 'apiUnavailable' }

export interface RouteMeta {
  /** Public routes (login, verify) are always reachable. */
  public?: boolean
}

export type RouteAccess = 'allow' | { redirect: string }

/** Pure guard decision. Never redirects to /login on backend-down (W8). */
export function resolveRouteAccess(
  state: AuthState,
  meta: RouteMeta,
): RouteAccess {
  if (meta.public) return 'allow'
  switch (state.status) {
    case 'authenticated':
      return 'allow'
    case 'unauthenticated':
      return { redirect: '/login' }
    case 'apiUnavailable':
      // Backend unreachable → render the reload screen in-place, don't bounce.
      return 'allow'
    case 'loading':
      return 'allow'
  }
}

/** Classify a `/me` failure into the non-success AuthState (W8). */
export function classifyAuthError(
  error: unknown,
): 'unauthenticated' | 'apiUnavailable' {
  if (error instanceof ApiError) {
    // ONLY a genuine auth rejection ends the session. Everything else — network
    // (0), timeout (408), rate limit (429), server errors (5xx) — is transient;
    // treating a 429 as "no session" was logging active users out mid-use.
    if (error.status === 401 || error.status === 403) return 'unauthenticated'
    return 'apiUnavailable'
  }
  return 'apiUnavailable'
}

/**
 * Bootstrap `/me` with an explicit refresh-once (the endpoint itself has retry
 * disabled). 401 → shared single-flight refresh → retry once; still failing
 * propagates the error for classification.
 */
export async function fetchMe(): Promise<UserProfileResponse> {
  try {
    return await getMe()
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      const outcome = await refresh()
      if (outcome === 'ok') return await getMe()
      // dev server with VITE_DEV_AUTO_LOGIN=1 only (see devAutoLogin)
      if (await devAutoLogin()) return await getMe()
    }
    throw e
  }
}

const PROACTIVE_REFRESH_MS = 300_000 // 5 min — well ahead of the 15-min access-token TTL, so a skipped/throttled tick (backgrounded tab) still leaves 2 more before expiry.

/**
 * Reactive auth state. Drives the layout + route guard. While authenticated it
 * proactively refreshes every 720s (deduped via the shared single-flight in
 * http.ts), so an active session never bounces mid-use.
 */
export function useAuth(): AuthState {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: qk.me(),
    queryFn: fetchMe,
    // Don't retry a real auth rejection (401/403) — log out promptly. DO retry a
    // transient (429/5xx/network) a couple times so a rate-limit blip on /me
    // self-heals instead of bouncing the user to /login.
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      ) {
        return false
      }
      return failureCount < 2
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })

  const authed = query.status === 'success'
  useEffect(() => {
    if (!authed) return
    const id = setInterval(() => {
      void refresh().then((outcome) => {
        // Lost the session in the background → re-evaluate /me (→ Unauthenticated).
        if (outcome === 'unauthenticated') {
          void qc.invalidateQueries({ queryKey: qk.me() })
        }
      })
    }, PROACTIVE_REFRESH_MS)
    return () => clearInterval(id)
  }, [authed, qc])

  if (query.status === 'pending') return { status: 'loading' }
  if (query.status === 'success') {
    return { status: 'authenticated', user: query.data }
  }
  return classifyAuthError(query.error) === 'apiUnavailable'
    ? { status: 'apiUnavailable' }
    : { status: 'unauthenticated' }
}
